import React from 'react';
import { Box, Chip, Skeleton, Typography } from '@mui/material';
import moment from 'moment';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SendIcon from '@mui/icons-material/Send';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaidIcon from '@mui/icons-material/Paid';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { dateMonthYearDateFormat } from '../../../constants';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

const STATUS_BORDER_COLORS = { 1: '#98BAF0', 2: '#E3D38D', 3: '#B8FFA4', 4: '#f94e35', 5: '#85CA85', 6: '#FDD9A0' };

const getTimelineIcon = (status) => {
    const s = { fontSize: 15 };
    if (status === 2) return <SendIcon sx={s} />;
    if (status === 3) return <TaskAltIcon sx={s} />;
    if (status === 4) return <CancelOutlinedIcon sx={s} />;
    if (status === 5) return <PaidIcon sx={s} />;
    if (status === 6) return <PublishedWithChangesIcon sx={s} />;
    return <EditOutlinedIcon sx={s} />;
};


const splitChangedAt = (changedAt) => {
    if (!changedAt) return { date: '' };
    const formatted = moment(changedAt).isValid() ? moment(changedAt).format(dateMonthYearDateFormat) : '';
    return { date: formatted };
};


const InvoiceTimelineCard = ({ statusTimelineInfo = [], skeletonLoader, invoice }) => {
    if (skeletonLoader) {
        return (
            <Box sx={{ px: 1, pt: 1, height: '310px', display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
                {[1, 2, 3, 4, 5].map((i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Skeleton variant="circular" width={28} height={28} sx={{ flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton variant="rounded" width="80%" height={18} sx={{ mb: 0.5 }} />
                            <Skeleton variant="text" width="60%" height={14} />
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    }

    if (!statusTimelineInfo?.length) {
        return (
            <Typography align="center" sx={{ py: 2, fontSize: '0.875rem', color: '#6b7280' }}>
                No Timeline Available
            </Typography>
        );
    }

    // Group only consecutive partial payments that have 2+ entries into a single tile
    const groupedPartialyPaid = [];
    let i = 0;
    while (i < statusTimelineInfo.length) {
        if (statusTimelineInfo[i].status === 5) {
            const items = [];
            while (i < statusTimelineInfo.length && statusTimelineInfo[i].status === 5) {
                items.push(statusTimelineInfo[i]);
                i++;
            }
            if (items.length > 1) {
                groupedPartialyPaid.push({ isPartialGroup: true, items });
            } else {
                groupedPartialyPaid.push({ isPartialGroup: false, item: items[0] });
            }
        } else {
            groupedPartialyPaid.push({ isPartialGroup: false, item: statusTimelineInfo[i] });
            i++;
        }
    }

    return (
        <Box sx={{ px: 0.5, pt: 0.5 }}>
            {groupedPartialyPaid.map((entry, gIndex) => {
                const isLast = gIndex === groupedPartialyPaid.length - 1;

                if (entry.isPartialGroup) {
                    const first = entry.items[0];
                    const relTimeLabel = first?.relative_timing;
                    return (
                        <Box key={gIndex} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', position: 'relative', mb: isLast ? 0 : 0.75 }}>
                            {!isLast && (
                                <Box sx={{ position: 'absolute', left: '12px', top: '26px', bottom: '-6px', width: '2px', bgcolor: '#d1d5db', borderRadius: 1, zIndex: 0 }} />
                            )}
                            <Box sx={{ display: 'flex', flexShrink: 0, position: 'relative', zIndex: 1 }}>
                                <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box className="invoice-status-5" sx={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <PaidIcon sx={{ fontSize: 15 }} />
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{ flex: 1, border: '1px solid #e5e7eb', borderLeft: `3px solid ${STATUS_BORDER_COLORS[5]}`, borderRadius: 1.5, p: '8px 10px', bgcolor: '#fff', minWidth: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Partial Payment Received</Typography>
                                </Box>
                                {entry.items.map((partialyPaid, partialyPaidIndex) => {
                                    const { date } = splitChangedAt(partialyPaid?.changed_at);
                                    return (
                                        <Box key={partialyPaidIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderTop: '1px solid #f3f4f6' }}>
                                            <Chip label={`Partial ${partialyPaidIndex + 1}`} size="small" sx={{ flexShrink: 0, height: 18, fontSize: '0.6rem', bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, border: '1px solid #f59e0b', '& .MuiChip-label': { px: 0.6 } }} />
                                            {partialyPaid?.changed_by_name ? <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', flex: 1 }}>{partialyPaid?.changed_by_name}</Typography> : null}
                                            {date ? <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', flexShrink: 0 }}>{date}</Typography> : null}
                                        </Box>
                                    );
                                })}
                                {relTimeLabel ? (
                                    <Box sx={{ mt: 0.5 }}>
                                        <Chip label={<Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}><AccessTimeIcon sx={{ fontSize: 11 }} />{relTimeLabel}</Box>} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb', '& .MuiChip-label': { px: 0.75, py: 0 } }} />
                                    </Box>
                                ) : null}
                            </Box>
                        </Box>
                    );
                }

                const item = entry.item;
                const relTimeLabel = item?.relative_timing;
                const { date } = splitChangedAt(item?.changed_at);

                return (
                    <Box key={gIndex} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', position: 'relative', mb: isLast ? 0 : 0.75 }}>
                        {!isLast && (
                            <Box sx={{ position: 'absolute', left: '12px', top: '26px', bottom: '-6px', width: '2px', bgcolor: '#d1d5db', borderRadius: 1, zIndex: 0 }} />
                        )}
                        <Box sx={{ display: 'flex', flexShrink: 0, position: 'relative', zIndex: 1 }}>
                            <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Box className={`invoice-status-${item.status}`} sx={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {getTimelineIcon(item.status)}
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1, border: '1px solid #e5e7eb', borderLeft: `3px solid ${STATUS_BORDER_COLORS[item.status] || '#e5e7eb'}`, borderRadius: 1.5, p: '8px 10px', bgcolor: '#fff', minWidth: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                        {item.status_label}
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                    {date ? <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', lineHeight: 1.3 }}>{date}</Typography> : null}
                                </Box>
                            </Box>
                            {item?.changed_by_name ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                    <PersonOutlineIcon sx={{ fontSize: 12, color: '#9ca3af' }} />
                                    <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>{item?.changed_by_name}</Typography>
                                </Box>
                            ) : null}
                            {relTimeLabel ? (
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={<Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}><AccessTimeIcon sx={{ fontSize: 11 }} />{relTimeLabel}</Box>}
                                        size="small"
                                        sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb', '& .MuiChip-label': { px: 0.75, py: 0 } }}
                                    />
                                </Box>
                            ) : null}
                            {item?.status_date ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', mt: 0.5 }}>
                                    <AccessTimeIcon sx={{ fontSize: 11, color: '#9ca3af' }} />
                                    <Typography sx={{ fontSize: '0.65rem', color: '#9ca3af' }}>
                                        {item?.status_label} Date: {moment(item?.status_date).format(dateMonthYearDateFormat)}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default InvoiceTimelineCard;